"use client";

import { useState } from "react";
import {
  CipherGrid,
  type CipherMode,
  CipherWorkbench,
  type GridTone,
} from "@/components/tool-kit/cipher-workbench";
import {
  ErrorNotice,
  Notice,
  PillGroup,
  TextField,
} from "@/components/tool-kit/primitives";
import { HINT, LABEL, TABLE, TD, TH } from "@/components/tool-kit/styles";
import { SquareOptions } from "@/components/tools/ciphers-polygraphic/square-options";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { tPlural } from "@/lib/i18n/interpolate";
import {
  type NihilistResult,
  type NihilistStep,
  nihilistTransform,
} from "@/lib/tool-kit/ciphers-polygraphic/nihilist";
import type {
  GridAlphabet,
  SquareFill,
} from "@/lib/tool-kit/ciphers-polygraphic/squares";

const STEP_LIMIT = 30;
const HEADERS = ["1", "2", "3", "4", "5"];

/** English strings. A translated content file overrides these through `ui`. */
export const NIHILIST_CIPHER_STRINGS = {
  mode: "Mode",
  encode: "Encode",
  decode: "Decode",
  plainText: "Plain text",
  cipherText: "Cipher text (numbers)",
  decodedText: "Decoded text",
  inputPlaceholder: "Type or paste your message",
  inputPlaceholderDecode: "Numbers separated by spaces or commas",
  sample: "Dynamite winter palace",
  keyword: "Square keyword",
  keywordSample: "ZEBRAS",
  keywordHint:
    "Its letters fill the 5 × 5 square first, each used once; the rest of the alphabet follows in order.",
  key: "Additive key",
  keySample: "RUSSIAN",
  keyHint:
    "A second keyword. Its letters are numbered from the same square, and the numbers repeat along the message.",
  kind: "Fitting 26 letters into 25 cells",
  mergeIJ: "I and J share a cell",
  dropQ: "Leave out Q",
  fill: "Write the keyword into the square",
  fillHint:
    "Along the rows is the usual way. Choose the route your puzzle or textbook used.",
  fillRows: "Along the rows",
  fillColumns: "Down the columns",
  fillSpiral: "In a clockwise spiral",
  wrap: "Totals of 100 and over",
  wrapFull: "Write in full (106) — Wikipedia",
  wrapDrop: "Drop the hundreds (06) — American Cryptogram Association",
  wrapHint:
    "Row and column numbers add up to between 22 and 110. Both ways of writing the totals above 99 are in use, and decoding needs the same one.",
  copy: "Copy result",
  copied: "Copied!",
  clear: "Clear",
  swap: "Swap input and output",
  outputPlaceholder: "The result appears here.",
  letterNote:
    "Only letters are enciphered. Spaces, digits and punctuation in the message are dropped, and decoded text is in capitals. Cipher numbers are separated by spaces.",
  ijNote: "I and J share a cell, so every J comes back as I.",
  droppedQ:
    "one: {count} Q was left out — this square has no Q.|other: {count} Qs were left out — this square has no Q.",
  keyMissing:
    "Enter an additive key with at least one letter to see the result.",
  invalid:
    "one: {count} number did not decode to a letter and is shown as a question mark — check the keyword, key and totals setting.|other: {count} numbers did not decode to a letter and are shown as question marks — check the keyword, key and totals setting.",
  working: "Square and sums",
  squareLabel: "Key square",
  squareHint:
    "Each letter's number is its row, then its column: S in row 1, column 1 is 11. Rows and columns are numbered from 1.",
  ruleEncode:
    "Encoding: replace each plain letter by its number and each key letter by its number, then add them. The key repeats along the message.",
  ruleDecode:
    "Decoding: subtract the key letter's number from each cipher number. The difference is a row and a column, which name the plain letter.",
  plainColumn: "Plain",
  numberColumn: "Number",
  keyColumn: "Key",
  keyNumberColumn: "Key number",
  sumColumn: "Sum",
  cipherColumn: "Cipher",
  cipherNumberColumn: "Cipher number",
  differenceColumn: "Difference",
  letterColumn: "Letter",
  stepLimit:
    "one: Showing the first number.|other: Showing the first {count} numbers.",
} as const;

type Strings = typeof NIHILIST_CIPHER_STRINGS;

function Row({ step, encoding }: { step: NihilistStep; encoding: boolean }) {
  if (encoding) {
    return (
      <>
        <td className={`${TD} font-mono`}>{step.plain}</td>
        <td className={`${TD} font-mono`}>{step.plainNumber}</td>
        <td className={`${TD} font-mono`}>{step.key}</td>
        <td className={`${TD} font-mono`}>{step.keyNumber}</td>
        <td className={`${TD} font-mono`}>{step.sum}</td>
        <td className={`${TD} font-mono`}>{step.written}</td>
      </>
    );
  }
  return (
    <>
      <td className={`${TD} font-mono`}>{step.written}</td>
      <td className={`${TD} font-mono`}>{step.sum}</td>
      <td className={`${TD} font-mono`}>{step.key}</td>
      <td className={`${TD} font-mono`}>{step.keyNumber}</td>
      <td className={`${TD} font-mono`}>{step.plainNumber ?? ""}</td>
      <td className={`${TD} font-mono`}>{step.plain}</td>
    </>
  );
}

function Working({
  result,
  encoding,
  strings,
}: {
  result: NihilistResult;
  encoding: boolean;
  strings: Strings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const shown = result.steps.slice(0, STEP_LIMIT);
  const used = new Set(result.steps.map((step) => step.plain));
  const keys = new Set(result.key);
  const tone = (index: number): GridTone | undefined => {
    const letter = result.square.charAt(index);
    if (keys.has(letter)) {
      return "secondary";
    }
    return used.has(letter) ? "primary" : undefined;
  };
  const head = encoding
    ? [
        strings.plainColumn,
        strings.numberColumn,
        strings.keyColumn,
        strings.keyNumberColumn,
        strings.sumColumn,
        strings.cipherColumn,
      ]
    : [
        strings.cipherNumberColumn,
        strings.sumColumn,
        strings.keyColumn,
        strings.keyNumberColumn,
        strings.differenceColumn,
        strings.letterColumn,
      ];
  return (
    <>
      <p className="text-muted-foreground text-sm leading-relaxed">
        {encoding ? strings.ruleEncode : strings.ruleDecode}
      </p>
      <div className="space-y-2">
        <p className={LABEL}>{strings.squareLabel}</p>
        <CipherGrid
          cells={[...result.square]}
          columnHeaders={HEADERS}
          columns={5}
          label={strings.squareLabel}
          rowHeaders={HEADERS}
          tone={tone}
        />
        <p className={HINT}>{strings.squareHint}</p>
      </div>
      {shown.length > 0 ? (
        <div className="space-y-2">
          <div className="overflow-x-auto rounded-lg border border-border bg-card">
            <table className={TABLE}>
              <thead>
                <tr>
                  {head.map((label) => (
                    <th className={TH} key={label} scope="col">
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {shown.map((step, index) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: steps are positional
                  <tr key={index}>
                    <Row encoding={encoding} step={step} />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {result.steps.length > STEP_LIMIT ? (
            <p className={HINT}>
              {tPlural(strings.stepLimit, locale, STEP_LIMIT, {
                count: fmt(STEP_LIMIT),
              })}
            </p>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

export default function NihilistCipherWidget() {
  const strings = useToolUi(NIHILIST_CIPHER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [mode, setMode] = useState<CipherMode>("encode");
  const [input, setInput] = useState<string>(strings.sample);
  const [keyword, setKeyword] = useState<string>(strings.keywordSample);
  const [key, setKey] = useState<string>(strings.keySample);
  const [kind, setKind] = useState<GridAlphabet>("merge-ij");
  const [fill, setFill] = useState<SquareFill>("rows");
  const [wrap, setWrap] = useState<"full" | "drop">("full");

  const encoding = mode === "encode";
  const result = nihilistTransform(
    input,
    {
      keyword,
      kind: kind === "drop-q" ? "drop-q" : "merge-ij",
      fill,
      key,
      wrap: wrap === "drop",
    },
    mode
  );

  const keyControls = (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          hint={strings.keywordHint}
          id="ni-keyword"
          label={strings.keyword}
          mono
          onChange={setKeyword}
          value={keyword}
        />
        <TextField
          hint={strings.keyHint}
          id="ni-key"
          label={strings.key}
          mono
          onChange={setKey}
          value={key}
        />
      </div>
      <SquareOptions
        fill={fill}
        fillHint={strings.fillHint}
        fillLabel={strings.fill}
        fills={[
          { value: "rows", label: strings.fillRows },
          { value: "columns", label: strings.fillColumns },
          { value: "spiral", label: strings.fillSpiral },
        ]}
        idPrefix="ni"
        kind={kind}
        kindLabel={strings.kind}
        kinds={[
          { value: "merge-ij", label: strings.mergeIJ },
          { value: "drop-q", label: strings.dropQ },
        ]}
        onFillChange={setFill}
        onKindChange={setKind}
      />
      <PillGroup
        label={strings.wrap}
        name="ni-wrap"
        onChange={setWrap}
        options={[
          { value: "full", label: strings.wrapFull },
          { value: "drop", label: strings.wrapDrop },
        ]}
        value={wrap}
      />
      <p className={HINT}>{strings.wrapHint}</p>
    </div>
  );

  const notices = (
    <>
      {result.key === "" ? <Notice>{strings.keyMissing}</Notice> : null}
      {result.droppedQ > 0 ? (
        <Notice>
          {tPlural(strings.droppedQ, locale, result.droppedQ, {
            count: fmt(result.droppedQ),
          })}
        </Notice>
      ) : null}
      {result.invalid > 0 ? (
        <ErrorNotice>
          {tPlural(strings.invalid, locale, result.invalid, {
            count: fmt(result.invalid),
          })}
        </ErrorNotice>
      ) : null}
    </>
  );

  return (
    <CipherWorkbench
      idPrefix="ni"
      input={input}
      keyControls={keyControls}
      labels={{
        mode: strings.mode,
        encode: strings.encode,
        decode: strings.decode,
        input: encoding ? strings.plainText : strings.cipherText,
        inputPlaceholder: encoding
          ? strings.inputPlaceholder
          : strings.inputPlaceholderDecode,
        output: encoding ? strings.cipherText : strings.decodedText,
        outputPlaceholder: strings.outputPlaceholder,
        copy: strings.copy,
        copied: strings.copied,
        clear: strings.clear,
        swap: strings.swap,
        working: strings.working,
      }}
      mode={mode}
      notices={notices}
      onInputChange={setInput}
      onModeChange={setMode}
      output={result.output}
      outputNotes={
        <div className="space-y-1">
          <p className={HINT}>{strings.letterNote}</p>
          {kind === "merge-ij" ? (
            <p className={HINT}>{strings.ijNote}</p>
          ) : null}
        </div>
      }
      working={
        result.key === "" ? null : (
          <Working encoding={encoding} result={result} strings={strings} />
        )
      }
    />
  );
}
